import { isEmpty, max, orderBy } from 'lodash-es';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  CompListing,
  ListingNote,
  ListingNoteType,
  Seating,
} from 'src/WebApiController';

import {
  CeilingDividerItem,
  DividerItem,
  FloorDividerItem,
  IdenticalCompListingGroup,
  ListingTileItem,
  ListingTileItemType,
  ListingWithComp,
  OwnListingTileItem,
  PriceRangeCompListingGroup,
  RangedCompListingGroupItem,
  SingleCompListing,
} from './ListingTilesV2.types';

// Type guards using enum values
export const isOwnListing = (
  item: ListingTileItem
): item is OwnListingTileItem => {
  return item?.type === ListingTileItemType.OwnListing;
};

export const isSingleCompListing = (
  item: ListingTileItem | RangedCompListingGroupItem
): item is SingleCompListing => {
  return item.type === ListingTileItemType.SingleComp;
};

export const isIdenticalCompListingGroup = (
  item: ListingTileItem | RangedCompListingGroupItem
): item is IdenticalCompListingGroup => {
  return item.type === ListingTileItemType.Identical;
};

export const isPriceRangeGroup = (
  item: ListingTileItem
): item is PriceRangeCompListingGroup => {
  return item.type === ListingTileItemType.PriceRange;
};

export const isFloorDivider = (
  item: ListingTileItem
): item is FloorDividerItem => {
  return item.type === ListingTileItemType.Floor;
};

export const isCeilingDivider = (
  item: ListingTileItem
): item is CeilingDividerItem => {
  return item.type === ListingTileItemType.Ceiling;
};

export const isDividerItem = (item: ListingTileItem): item is DividerItem => {
  return (
    item.type === ListingTileItemType.Floor ||
    item.type === ListingTileItemType.Ceiling
  );
};

export const mapCompListingToListing = (
  listing: CompListing
): ListingWithComp => {
  return {
    id: listing.listingId,
    availQty: listing.availableTickets ?? undefined,
    unsoldQty: listing.availableTickets ?? undefined,
    seating: {
      section: listing.section ?? '',
      row: listing.row,
      rowId: listing.rowId,
      seatFr: listing.seatFrom,
      seatTo: listing.seatTo,
    } as Seating,
    allInPrice: listing.sellerAllInPrice?.amt ?? undefined,
    currency:
      listing.sellerAllInPrice?.currency ?? listing.currencyCode ?? 'USD',
    isCompListing: true,
    listingNotes: listing.listingNotes,
    isFanInventory: listing.isFanInventory,
    validPurchaseQuantities: listing.validPurchaseQuantities,
  } as ListingWithComp;
};

/**
 * Groups comp listings between own listings, creating multiple groups as needed
 * @param inventoryListings - Combined array of own and comp listings sorted by price
 * @param floorPrice - Optional floor price threshold for separating comp listings
 * @param ceilingPrice - Optional ceiling price threshold for separating comp listings
 * @returns Array of items that are either own Listings or CompListingGroups
 */
export const groupCompListingsBetweenOwnListings = (
  inventoryListings: ListingWithComp[],
  floorPrice?: number | null,
  ceilingPrice?: number | null
): Array<ListingTileItem> => {
  // Step 1: Group listings into segments separated by own listings
  const segments = groupListingsIntoSegments(
    inventoryListings,
    floorPrice,
    ceilingPrice
  );

  // Step 2: Process each segment and add appropriate dividers
  const result = processSegmentsWithDividers(
    segments,
    floorPrice,
    ceilingPrice
  );

  return result;
};

type ListingSegment = {
  type: 'comp' | 'own';
  listings: ListingWithComp[];
};

/**
 * Groups listings into segments separated by own listings
 * Example: [comp, comp, own, comp, own] → [compSegment, ownSegment, compSegment, ownSegment]
 */
const groupListingsIntoSegments = (
  inventoryListings: ListingWithComp[],
  floorPrice?: number | null,
  ceilingPrice?: number | null
): ListingSegment[] => {
  const segments: ListingSegment[] = [];
  let currentCompListings: ListingWithComp[] = [];

  const flushCompListingSegment = (compListings: ListingWithComp[]) => {
    if (compListings.length > 0) {
      const flushedSegements = splitCompListingsByPrice(
        compListings,
        floorPrice,
        ceilingPrice
      );
      segments.push(...flushedSegements);
      currentCompListings = [];
    }
  };

  for (const listing of inventoryListings) {
    if (listing.isCompListing) {
      currentCompListings.push(listing);
    } else {
      // Own listing found - flush any pending comp listings first
      flushCompListingSegment(currentCompListings);

      // Add the own listing as its own segment
      segments.push({ type: 'own', listings: [listing] });
    }
  }

  // Add any remaining comp listings at the end
  flushCompListingSegment(currentCompListings);

  return orderBy(segments, (segment) =>
    max(segment.listings.map((l) => l.allInPrice ?? 0))
  );
};

/**
 * Splits comp listings into separate segments based on floor and ceiling price thresholds
 * Creates separate segments for:
 * - Listings below floor price (if floor exists)
 * - Listings between floor and ceiling (if both exist)
 * - Listings above ceiling price (if ceiling exists)
 * - All listings in one segment (if no price thresholds)
 */
const splitCompListingsByPrice = (
  compListings: ListingWithComp[],
  floorPrice?: number | null,
  ceilingPrice?: number | null
): ListingSegment[] => {
  if (!floorPrice && !ceilingPrice) {
    // No price thresholds - return all as one segment
    return [{ type: 'comp', listings: compListings }];
  }

  const segments: ListingSegment[] = [];
  const belowFloor: ListingWithComp[] = [];
  const betweenFloorAndCeiling: ListingWithComp[] = [];
  const aboveCeiling: ListingWithComp[] = [];

  for (const listing of compListings) {
    const price = listing.allInPrice || 0;

    if (floorPrice != null && price < floorPrice) {
      belowFloor.push(listing);
    } else if (ceilingPrice != null && price > ceilingPrice) {
      aboveCeiling.push(listing);
    } else {
      betweenFloorAndCeiling.push(listing);
    }
  }

  // Add segments in price order (lowest to highest)
  if (belowFloor.length > 0) {
    segments.push({ type: 'comp', listings: belowFloor });
  }
  if (betweenFloorAndCeiling.length > 0) {
    segments.push({ type: 'comp', listings: betweenFloorAndCeiling });
  }
  if (aboveCeiling.length > 0) {
    segments.push({ type: 'comp', listings: aboveCeiling });
  }

  return segments;
};

/**
 * Processes segments and adds appropriate dividers based on price thresholds
 */
const processSegmentsWithDividers = (
  segments: ListingSegment[],
  floorPrice?: number | null,
  ceilingPrice?: number | null
): ListingTileItem[] => {
  const result: ListingTileItem[] = [];
  let floorDividerAdded = false;
  let ceilingDividerAdded = false;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];

    if (segment.type === 'comp') {
      // Add floor divider before comp segment if needed
      if (
        !floorDividerAdded &&
        shouldAddFloorDivider(segment.listings, floorPrice)
      ) {
        result.push(createFloorDivider(floorPrice!));
        floorDividerAdded = true;
      }

      // Process comp listings into groups or single listings
      if (segment.listings.length === 1) {
        // Single comp listing
        result.push({
          type: ListingTileItemType.SingleComp,
          listing: segment.listings[0],
        });
      } else {
        // Multiple comp listings - create price range group
        const prices = segment.listings.map(
          (listing) => listing.allInPrice || 0
        );
        result.push({
          type: ListingTileItemType.PriceRange,
          groupItems: groupIdenticalCompListings(segment.listings),
          lowerPrice: Math.min(...prices),
          upperPrice: Math.max(...prices),
        });
      }

      // Add ceiling divider after comp segment if needed and this is the last segment
      if (
        !ceilingDividerAdded &&
        i === segments.length - 1 &&
        shouldAddCeilingDivider(segment.listings, ceilingPrice)
      ) {
        result.push(createCeilingDivider(ceilingPrice!));
        ceilingDividerAdded = true;
      }
    } else {
      // Own listing segment
      const ownListing = segment.listings[0];

      // Add floor divider before own listing if needed
      if (
        !floorDividerAdded &&
        shouldAddFloorDividerForOwnListing(ownListing, floorPrice)
      ) {
        result.push(createFloorDivider(floorPrice!));
        floorDividerAdded = true;
      }

      // Add the own listing
      result.push({
        type: ListingTileItemType.OwnListing,
        listing: ownListing,
      });

      // Add ceiling divider after own listing if needed and this is the last segment
      if (
        !ceilingDividerAdded &&
        i === segments.length - 1 &&
        shouldAddCeilingDividerForOwnListing(ownListing, ceilingPrice)
      ) {
        result.push(createCeilingDivider(ceilingPrice!));
        ceilingDividerAdded = true;
      }
    }
  }

  return result;
};

// Simple helper functions for creating dividers
const createFloorDivider = (price: number): ListingTileItem => ({
  type: ListingTileItemType.Floor,
  price,
});

const createCeilingDivider = (price: number): ListingTileItem => ({
  type: ListingTileItemType.Ceiling,
  price,
});

// Simplified condition checking functions
const shouldAddFloorDivider = (
  compListings: ListingWithComp[],
  floorPrice?: number | null
): boolean => {
  if (floorPrice == undefined) return false;
  return compListings.some(
    (listing) => (listing.allInPrice || 0) >= floorPrice
  );
};

const shouldAddCeilingDivider = (
  compListings: ListingWithComp[],
  ceilingPrice?: number | null
): boolean => {
  if (ceilingPrice == undefined) return false;
  return compListings.some(
    (listing) => (listing.allInPrice || 0) <= ceilingPrice
  );
};

const shouldAddFloorDividerForOwnListing = (
  ownListing: ListingWithComp,
  floorPrice?: number | null
): boolean => {
  if (floorPrice == undefined) return false;
  return (ownListing.allInPrice || 0) >= floorPrice;
};

const shouldAddCeilingDividerForOwnListing = (
  ownListing: ListingWithComp,
  ceilingPrice?: number | null
): boolean => {
  if (ceilingPrice == undefined) return false;
  return (ownListing.allInPrice || 0) <= ceilingPrice;
};

/**
 * Groups identical comp listings within a price range group (second layer)
 * @param compListings - Array of comp listings to group
 * @returns Array of second-layer items (SingleCompListing or IdenticalCompListingGroup)
 */
export const groupIdenticalCompListings = (
  compListings: ListingWithComp[]
): Array<SingleCompListing | IdenticalCompListingGroup> => {
  if (compListings.length === 0) return [];

  const groups: Array<SingleCompListing | IdenticalCompListingGroup> = [];
  const processed = new Set<number>();

  for (let i = 0; i < compListings.length; i++) {
    if (processed.has(i)) continue;

    const currentListing = compListings[i];
    const identicalListings = [currentListing];
    processed.add(i);

    // Find all identical listings
    for (let j = i + 1; j < compListings.length; j++) {
      if (processed.has(j)) continue;

      if (areCompListingsIdentical(compListings[j], currentListing)) {
        identicalListings.push(compListings[j]);
        processed.add(j);
      }
    }

    if (identicalListings.length >= 2) {
      // Create identical group for 2+ listings
      groups.push({
        type: ListingTileItemType.Identical,
        compListings: identicalListings,
      });
    } else {
      // Create single comp listing for individual listing
      groups.push({
        type: ListingTileItemType.SingleComp,
        listing: currentListing,
      });
    }
  }

  return groups;
};

const areCompListingsIdentical = (
  cl1: ListingWithComp,
  cl2: ListingWithComp
): boolean => {
  return (
    cl1.seating?.section === cl2.seating?.section &&
    cl1.seating?.row === cl2.seating?.row &&
    cl1.seating?.seatFr === cl2.seating?.seatFr &&
    cl1.seating?.seatTo === cl2.seating?.seatTo &&
    cl1.availQty === cl2.availQty &&
    cl1.allInPrice === cl2.allInPrice &&
    cl1.isFanInventory === cl2.isFanInventory &&
    areSameListingNoteCategory(cl1, cl2)
  );
};

const areSameListingNoteCategory = (
  cl1: ListingWithComp,
  cl2: ListingWithComp
): boolean => {
  const [isDefect1, isPerk1] = toListingNoteCategory(cl1.listingNotes ?? []);
  const [isDefect2, isPerk2] = toListingNoteCategory(cl2.listingNotes ?? []);
  return isDefect1 === isDefect2 && isPerk1 == isPerk2;
};

const toListingNoteCategory = (notes: ListingNote[]) => {
  let isDefect = false;
  let isPerk = false;
  for (const note of notes ?? []) {
    if (
      note.type === ListingNoteType.Defect ||
      note.type === ListingNoteType.RestrictionOnUse
    ) {
      isDefect = true;
    }
    if (
      note.type === ListingNoteType.Perk ||
      note.type === ListingNoteType.Information
    ) {
      isPerk = true;
    }
  }
  return [isDefect, isPerk];
};

/**
 * Function to filter POS listing/listing group with venue section ids
 *
 * For the listing group, we will not touch the listings within,
 * but exclude the listing group when all listings has sections not included in the section ids
 *
 * @param listing POS listing or listing group
 * @param sectionIds venue section ids
 * @param sectionNames venue section names
 */
export const filterListingBySection =
  (sectionIds: Set<number>, sectionNames: Set<string>) =>
  (listing: ListingWithComp) => {
    if (listing.isCompListing) {
      return false;
    }
    if (isEmpty(sectionIds) || isEmpty(sectionNames)) {
      return true;
    }

    // Keep the group if any of the listing is within the section filter
    if (listing.isLtGrp) {
      const flattenedListings = flattenListingGroup(listing);
      return flattenedListings.some(
        ({ seating }) =>
          (seating.sectionId && sectionIds.has(seating.sectionId)) ||
          sectionNames.has(seating.section)
      );
    }

    return (
      (listing.seating.sectionId &&
        sectionIds.has(listing.seating.sectionId)) ||
      sectionNames.has(listing.seating.section)
    );
  };
